import { Box } from '@mui/material';
import type { PlanNodeStatus } from '@timelog/pm-shared';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { STATUS_STYLES } from '../StatusPill';

import { BAR_HEIGHT_PX, RESIZE_HANDLE_PX, TIMELINE_ROW_HEIGHT_PX } from './timelineGeometry';
import { barGeometry } from './timelineModel';
import type { TimelineSpan } from './timelineModel';

import { MonoText } from '@/components/ui';

export type BarDragMode = 'move' | 'start' | 'end';

export type TimelineBarProps = {
  span: TimelineSpan;
  status: PlanNodeStatus;
  /** A node with children: the bar is its own dates unioned with theirs. */
  isParent: boolean;
  /** Where the children sit inside a parent's bar; null on a leaf. */
  childrenSpan?: TimelineSpan | null;
  /** Drawn from today → target with nothing stored yet (ADR 0050). */
  provisional?: boolean;
  dragging: boolean;
  /** Current zoom, px per day. */
  dayWidth: number;
  label: string;
  days: number;
  startIso: string;
  endIso: string;
  onPointerDown: (mode: BarDragMode) => (event: ReactPointerEvent<HTMLElement>) => void;
};

/**
 * One bar, with invisible resize handles at each end.
 *
 * Parents resize too (ADR 0020): an edge writes the node's own start or end,
 * and the bar cannot be shrunk past the children — which is why a parent shows a
 * band marking where they sit, so the slack either side of them is visible.
 */
export const TimelineBar = ({
  span,
  status,
  isParent,
  childrenSpan,
  provisional = false,
  dragging,
  dayWidth,
  label,
  days,
  startIso,
  endIso,
  onPointerDown,
}: TimelineBarProps) => {
  const { t } = useTranslation('pmPlanning');
  const { left, width } = barGeometry(span, dayWidth);
  /*
    Two full-width handles on a short bar would leave no body to grab, and for a
    parent the body drag IS the group move. Scaling them keeps a third of any bar
    grabbable at any width, with no cliff.
  */
  const handleWidth = Math.min(RESIZE_HANDLE_PX, Math.floor(width / 3));
  const children =
    isParent && childrenSpan
      ? {
          left: (childrenSpan.startOffset - span.startOffset) * dayWidth,
          width: (childrenSpan.endOffset - childrenSpan.startOffset + 1) * dayWidth,
          days: childrenSpan.endOffset - childrenSpan.startOffset + 1,
        }
      : null;
  const hatchedDays = isParent ? span.endOffset - span.startOffset + 1 - (children?.days ?? 0) : 0;
  const style = STATUS_STYLES[status];
  /*
    A parent's bar is hatched pink (v5, decision 9): the hatch is the parent's
    own frame, and the flat cover painted over the children's extent leaves
    only the over-stretch reading as hatched — the variance is visible rather
    than hidden. `planned`'s pill background is the sunken surface, which
    disappears against the track — fog reads as a bar there.
  */
  const background = isParent
    ? 'repeating-linear-gradient(45deg, var(--color-pink-60) 0 1.5px, transparent 1.5px 6px), var(--color-pink-10)'
    : status === 'planned'
      ? 'var(--color-fog-20)'
      : style.bg;

  return (
    <Box
      data-automation-id="timeline-bar"
      data-days={days}
      data-parent={isParent}
      data-provisional={provisional}
      data-start-iso={startIso}
      data-end-iso={endIso}
      data-hatched-days={isParent ? hatchedDays : undefined}
      onPointerDown={onPointerDown('move')}
      title={label}
      sx={{
        position: 'absolute',
        top: `${String((TIMELINE_ROW_HEIGHT_PX - BAR_HEIGHT_PX) / 2)}px`,
        height: BAR_HEIGHT_PX,
        left: `${String(left)}px`,
        width: `${String(width)}px`,
        borderRadius: 'var(--border-radius-small)',
        background,
        /*
          Dashed while the bar is only today → target: it looks like the plan,
          but no one has placed it yet. The first drag writes real dates and
          the border turns solid.
        */
        border: `1px ${provisional ? 'dashed' : 'solid'} ${isParent ? 'var(--color-pink-80)' : style.dot}`,
        display: 'flex',
        alignItems: 'center',
        px: 0.75,
        overflow: 'hidden',
        cursor: dragging ? 'grabbing' : 'grab',
        boxShadow: dragging ? 'var(--shadow-small)' : 'none',
        touchAction: 'none',
      }}
    >
      {/*
        Where the children actually sit: a flat cover over the hatch, so only
        the parent's own over-stretch keeps the hatched texture.
      */}
      {children && (
        <Box
          data-automation-id="timeline-bar-children"
          data-days={children.days}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${String(children.left)}px`,
            width: `${String(children.width)}px`,
            bgcolor: 'var(--color-pink-10)',
            pointerEvents: 'none',
          }}
        />
      )}
      <Box
        data-automation-id="timeline-bar-resize-start"
        onPointerDown={onPointerDown('start')}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: handleWidth,
          cursor: 'ew-resize',
          touchAction: 'none',
        }}
      />
      <Box
        data-automation-id="timeline-bar-resize-end"
        onPointerDown={onPointerDown('end')}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: handleWidth,
          cursor: 'ew-resize',
          touchAction: 'none',
        }}
      />

      <MonoText size="label" color={isParent ? 'var(--color-pink-100)' : style.fg}>
        {/* pointer-events off so the label never swallows a drag */}
        <Box component="span" sx={{ pointerEvents: 'none', whiteSpace: 'nowrap' }}>
          {t('timeline.DaysShort', { count: days })}
        </Box>
      </MonoText>
    </Box>
  );
};
